import {
  Link,
  PhotoNeighborhoods as PhotoNeighborhoodsIcon,
} from '@amzn/ring-ui-react-components';
import { DropzoneOptions, DropzoneState, useDropzone } from 'react-dropzone';
import { useIsNpssUser } from 'src/auth';
import { useIsDesktop } from 'src/shared/hooks';
import { isLocalBuild } from 'src/utils';
import { Box } from '../Box';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { Icon } from '../Icon';

const defaultOptions = {
  accept: ['image/jpeg', 'image/png', 'video/mp4'],
} as DropzoneOptions;

export type DropzoneProps = DropzoneOptions &
  Pick<DropzoneContentProps, 'hasFiles' | 'error'>;

export const Dropzone = ({ hasFiles, error, ...options }: DropzoneProps) => {
  const { getInputProps, getRootProps, isDragAccept, isDragReject } =
    useDropzone({ ...defaultOptions, ...options });

  const isDesktop = useIsDesktop();
  const hasFilesAndIsDesktop = hasFiles && isDesktop;

  return (
    <>
      <Flex
        {...getBgAndBorderColor({
          error,
          isDragAccept,
          isDragReject,
        })}
        {...getRootProps()}
        py={hasFilesAndIsDesktop ? 4 : 6}
        flex={1}
        width="100%"
        alignItems="center"
        borderRadius="round"
        borderStyle="dashed"
        borderWidth="2px"
        cursor="pointer"
        flexDirection={isDesktop ? 'column' : 'row'}
        justifyContent="center"
        overflow="hidden"
        textAlign="center"
        transition="all 250ms ease"
        _focus={{
          outline: 'none',
        }}
        _hover={{
          borderColor: 'primary.500',
        }}
        data-testid="dropzone"
      >
        <input {...getInputProps()} data-testid="dropzone-input" />
        {!isDesktop ? (
          <Flex>
            <PhotoNeighborhoodsIcon color="primary.400" boxSize="24px" />
            <Link ml={2} textDecoration="underline">
              Select Files from Device
            </Link>
          </Flex>
        ) : (
          <DropzoneContent hasFiles={hasFiles} error={error} />
        )}
      </Flex>
      {error && !isDesktop && (
        <Box
          textStyle="body-normal-regular"
          color="negative.500"
          whiteSpace="pre-wrap"
        >
          {error}
        </Box>
      )}
    </>
  );
};

if (isLocalBuild) {
  Dropzone.displayName = 'Dropzone';
}

const getBgAndBorderColor = ({
  error,
  isDragAccept,
  isDragReject,
}: Pick<DropzoneProps, 'error'> &
  Pick<DropzoneState, 'isDragAccept' | 'isDragReject'>) => {
  if (isDragAccept) {
    return {
      bg: 'primary.100',
      borderColor: 'primary.500',
    };
  }

  if (error || isDragReject) {
    return {
      bg: 'tertiary.300',
      borderColor: 'negative.500',
    };
  }

  return {
    bg: 'neutral.50',
    borderColor: 'tertiary.500',
  };
};

interface DropzoneContentProps {
  hasFiles: boolean;
  error?: string;
}

const DropzoneContent = ({ hasFiles, error }: DropzoneContentProps) => {
  const isDesktop = useIsDesktop();
  const isNpssUser = useIsNpssUser();
  const hasFilesAndIsDesktop = hasFiles && isDesktop;

  // TODO: make these variables const
  // after the enableUploadFromRingHistory feature is rolled out completely
  let titleWhenHasItems = isDesktop
    ? 'Drag and drop more files here'
    : 'Select More Files';

  let titleWhenIsEmpty = isDesktop
    ? 'Drag and drop your files here'
    : 'Select Files from your Device';

  if (!isNpssUser) {
    titleWhenHasItems = 'Select Files from Device';
    titleWhenIsEmpty = isDesktop
      ? 'Drag and drop your files here'
      : 'Select Files from Device';
  }

  return (
    <Flex
      alignItems="center"
      flexDirection={hasFilesAndIsDesktop ? 'row' : 'column'}
      justifyContent={
        hasFilesAndIsDesktop
          ? !isNpssUser
            ? 'center'
            : 'space-between'
          : 'center'
      }
      px={5}
      py={hasFilesAndIsDesktop ? 4 : 6}
      width={1}
    >
      <Flex
        alignItems={!hasFiles ? 'center' : 'flex-start'}
        flexDirection={hasFilesAndIsDesktop ? 'row' : 'column'}
        mb={hasFilesAndIsDesktop ? 0 : !isNpssUser ? 2 : 5}
      >
        {!isNpssUser ? (
          hasFiles ? (
            <PhotoNeighborhoodsIcon color="primary.400" boxSize="24px" />
          ) : (
            <PhotoNeighborhoodsIcon color="primary.500" boxSize="48px" />
          )
        ) : (
          <Icon name="photo_neighborhoods" size={32} />
        )}
        <Box
          ml={hasFilesAndIsDesktop ? (!isNpssUser ? 2 : 5) : 0}
          mt={hasFilesAndIsDesktop ? 0 : !isNpssUser ? 1 : 2}
          textAlign={hasFilesAndIsDesktop ? 'left' : 'center'}
        >
          <Box textStyle="body-normal-medium">
            {hasFiles ? (
              !isNpssUser ? (
                <Link textDecoration="underline">{titleWhenHasItems}</Link>
              ) : (
                titleWhenHasItems
              )
            ) : (
              titleWhenIsEmpty
            )}
          </Box>
          {!isNpssUser ? (
            error && (
              <Box
                textStyle="body-normal-regular"
                color="negative.500"
                whiteSpace="pre-wrap"
              >
                {error}
              </Box>
            )
          ) : error ? (
            <Box
              textStyle="body-normal-regular"
              color="negative.500"
              whiteSpace="pre-wrap"
            >
              {error}
            </Box>
          ) : (
            <Box
              textStyle={hasFiles ? 'body-small-light' : 'body-normal-light'}
            >
              You can add up to 5 photos and videos
            </Box>
          )}
          <Box
            display={hasFiles || !isDesktop ? 'none' : 'block'}
            mt={!isNpssUser ? 0 : 3}
            textStyle="body-normal-medium"
          >
            {!isNpssUser ? 'or' : 'Or'}
          </Box>
        </Box>
      </Flex>
      {hasFiles ? (
        isNpssUser && <Link textDecoration="underline">Or Select Files</Link>
      ) : (
        <Button
          px={7}
          py={2}
          size="lg"
          variant={hasFiles ? 'empty' : 'primary'}
          borderRadius={8}
        >
          Select Files
        </Button>
      )}
    </Flex>
  );
};
